import clsx from 'clsx';
import { Fragment, useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';

import { Button, Icon, TextField, Typography } from '@/ui';

import { LoadError, PageLoading } from '../PageState';
import RunStatusBanner from '../RunStatusBanner';
import { Toast, useToast } from '../Toast';
import { reflectionPath, updatedLabel } from '../format';

import { parseCategoryParam, resolveSection } from './notesSections';
import {
  parseEditText,
  sourceLabels,
  toEditText,
  toReplaceLines,
} from './notesText';

import type { NoteItem } from '@/store/api/mcpMemberApi';
import type { FC } from 'react';

import {
  useGetCoachingNotesQuery,
  useReplaceCoachingNotesCategoryMutation,
  useRestoreCoachingNotesCategoryMutation,
} from '@/store/api/mcpMemberApi';

const LIST_PATH = '/m/settings/coaching-notes';

const SourceTags: FC<{ item: NoteItem }> = ({ item }) => {
  const labels = sourceLabels(item);
  if (labels.length === 0) return null;
  return (
    <span className="text-sm text-cement-300">
      {item.sources.length > 0
        ? item.sources.map((source, index) => (
            <Fragment key={source.session_id}>
              {index > 0 && ' · '}
              <Link className="text-sage-500" to={reflectionPath(source)}>
                {source.label}
              </Link>
            </Fragment>
          ))
        : labels.join(' · ')}
    </span>
  );
};

const CoachingNotesCategoryPage = () => {
  const { category } = useParams<{ category: string }>();
  const ref = parseCategoryParam(category);
  const [searchParams] = useSearchParams();
  const freshId = searchParams.get('fresh');
  const notes = useGetCoachingNotesQuery();
  const [replaceCategory, { isLoading: saving }] =
    useReplaceCoachingNotesCategoryMutation();
  const [restoreCategory] = useRestoreCoachingNotesCategoryMutation();
  const { close, show, toast } = useToast();
  const [draft, setDraft] = useState<string | null>(null);
  // The items the draft was built from. Lines map onto these; anything that
  // appears later (a run finishing mid-edit) is kept, never tombstoned.
  const [editBase, setEditBase] = useState<NoteItem[]>([]);

  useEffect(() => {
    if (freshId && notes.data) {
      document
        .getElementById(`note-${freshId}`)
        ?.scrollIntoView({ block: 'center' });
    }
  }, [freshId, notes.data]);

  if (!ref) return <Navigate to={LIST_PATH} replace />;
  if (notes.isLoading) return <PageLoading />;
  if (notes.isError || !notes.data) {
    return (
      <LoadError
        onRetry={() => {
          notes.refetch().catch(() => undefined);
        }}
      />
    );
  }
  const section = resolveSection(notes.data, ref);
  if (!section) return <Navigate to={LIST_PATH} replace />;

  const runInFlight =
    notes.data.run?.status === 'queued' || notes.data.run?.status === 'running';

  const save = async (value: string) => {
    const before = section.items;
    const target = { category: section.category, person_id: section.personId };
    const seen = new Set(editBase.map((item) => item.id));
    const lines = [
      ...toReplaceLines(editBase, parseEditText(value)),
      ...before
        .filter((item) => !seen.has(item.id))
        .map((item) => ({ id: item.id, text: item.text })),
    ];
    try {
      await replaceCategory({ ...target, lines }).unwrap();
    } catch {
      show('Couldn’t save. Try again.'); // NEW COPY
      return;
    }
    setDraft(null);
    show('Saved. Your AI will see the new version.', () => {
      restoreCategory({ ...target, items: before })
        .unwrap()
        .catch(() => show('Couldn’t undo. Try again.')); // NEW COPY
    });
  };

  return (
    <div>
      <Link
        className="mb-4 inline-flex items-center gap-1 text-sage-500 no-underline"
        to={LIST_PATH}
      >
        <Icon>arrow_back</Icon> Coaching notes
      </Link>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <Typography component="h1" variant="h4">
            {section.name}
          </Typography>
          <Typography className="mt-1 text-cement-400" variant="body1">
            {section.description}
          </Typography>
          <Typography className="text-cement-300" variant="caption">
            {updatedLabel(section.updatedAt)}
          </Typography>
        </div>
        {draft === null && (
          <Button
            disabled={notes.isFetching || runInFlight}
            size="small"
            variant="outlined"
            onClick={() => {
              setEditBase(section.items);
              setDraft(toEditText(section.items));
            }}
          >
            Edit
          </Button>
        )}
      </div>
      <RunStatusBanner run={notes.data.run} />
      {draft === null ? (
        <div className="flex flex-col gap-3">
          {section.items.length === 0 && (
            <p className="text-cement-400">Nothing here right now.</p>
          )}
          {section.items.map((item) => (
            <p
              key={item.id}
              className={clsx(
                'm-0 rounded-lg px-2 py-1 text-[15.5px] leading-relaxed',
                { 'bg-sunny-300': item.id === freshId },
              )}
              data-fresh={item.id === freshId ? 'true' : undefined}
              id={`note-${item.id}`}
            >
              {item.text} <SourceTags item={item} />
            </p>
          ))}
        </div>
      ) : (
        <div>
          <TextField
            inputProps={{ 'aria-label': `Edit ${section.name}` }}
            minRows={12}
            value={draft}
            fullWidth
            multiline
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="mt-3 flex items-center gap-2">
            <span className="mr-auto text-[13px] text-cement-300">
              One note per line. Sessions in brackets stay linked.
            </span>
            <Button size="small" variant="text" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={saving}
              size="small"
              variant="contained"
              onClick={() => {
                save(draft).catch(() => undefined);
              }}
            >
              Save
            </Button>
          </div>
        </div>
      )}
      <Toast toast={toast} onClose={close} />
    </div>
  );
};

export default CoachingNotesCategoryPage;
